import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Compass, Loader2, ShieldAlert, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import ClinqLogo from "../components/ClinqLogo";

const ScoreRing = ({ score, color }) => {
  const r = 54, c = 2 * Math.PI * r, pct = Math.max(0, Math.min(100, score)) / 100;
  return (
    <svg viewBox="0 0 128 128" className="h-40 w-40" data-testid="welcome-snapshot-ring">
      <circle cx="64" cy="64" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
      <circle cx="64" cy="64" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform="rotate(-90 64 64)"
        style={{ transition: "stroke-dashoffset 1.2s ease-out" }} />
      <text x="64" y="60" textAnchor="middle" fill="#fff" fontSize="34" fontWeight="800" fontFamily="Manrope, sans-serif">{score}</text>
      <text x="64" y="80" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">out of 100</text>
    </svg>
  );
};

const Section = ({ n, Icon, title, children, testId }) => (
  <section className="mt-10" data-testid={testId}>
    <div className="flex items-center gap-3">
      <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-400">Section {n}</span>
    </div>
    <h2 className="mt-2 flex items-center gap-2 text-lg font-extrabold text-white md:text-xl" style={{ fontFamily: "Manrope, sans-serif" }}>
      <Icon className="h-5 w-5 text-emerald-300" /> {title}
    </h2>
    <div className="mt-4">{children}</div>
  </section>
);

export default function WelcomeSnapshot() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/founder/welcome-snapshot")
      .then(({ data }) => { setSnap(data); api.post("/founder/welcome-snapshot/seen").catch(() => {}); })
      .catch(() => setErr("We couldn't build your snapshot right now."));
  }, []);

  const goReport = async () => {
    try { await api.post("/founder/welcome-snapshot/seen"); } catch (_) { /* ignore */ }
    try { await refresh(); } catch (_) { /* ignore */ }
    nav("/onboarding", { replace: true });
  };

  if (err) {
    return (
      <div className="min-h-screen bg-[#04302E] px-6 py-12 text-white">
        <p className="text-white/70" data-testid="welcome-snapshot-error">{err}</p>
        <button onClick={goReport} className="mt-4 rounded-md bg-[#0A8B85] px-5 py-3 text-sm font-semibold">Continue to my report</button>
      </div>
    );
  }
  if (!snap) {
    return <div className="flex min-h-screen items-center justify-center bg-[#04302E] text-white/70"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Building your snapshot…</div>;
  }

  return (
    <div className="min-h-screen bg-[#04302E] text-white" data-testid="welcome-snapshot-page">
      <div className="mx-auto max-w-3xl px-6 py-10 sm:px-10">
        <ClinqLogo />
        <p className="mt-10 text-[11px] font-bold uppercase tracking-[0.18em] text-white/50">
          {snap.first_name ? `${snap.first_name}, ` : ""}your freemium business snapshot
        </p>
        <h1 className="mt-3 text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-5xl" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="welcome-snapshot-heading">
          Here is where your business stands right now.
        </h1>

        <Section n={1} Icon={Sparkles} title="Your Capital Readiness Score" testId="welcome-snapshot-score">
          <div className="flex flex-col items-start gap-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:flex-row sm:items-center">
            <ScoreRing score={snap.score} color={snap.band.color} />
            <div>
              <span className="inline-block rounded-full border px-3 py-1 text-xs font-extrabold uppercase tracking-wide"
                style={{ borderColor: snap.band.color, color: snap.band.color }} data-testid="welcome-snapshot-band">{snap.band.label}</span>
              <p className="mt-3 text-base leading-relaxed text-white/80" data-testid="welcome-snapshot-interpretation">{snap.band.interpretation}</p>
              <p className="mt-2 text-xs text-white/45">Indicative — based on your quiz answers, not yet verified against documents.</p>
            </div>
          </div>
        </Section>

        <Section n={2} Icon={Compass} title="What this tells us about your business" testId="welcome-snapshot-about">
          <div className="space-y-3 text-base leading-relaxed text-white/80">
            {snap.about.map((s, i) => <p key={i}>{s}</p>)}
          </div>
        </Section>

        <Section n={3} Icon={ShieldAlert} title="The key things holding you back" testId="welcome-snapshot-gaps">
          <ol className="space-y-3">
            {snap.gaps.map((g, i) => (
              <li key={i} className="flex gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`welcome-snapshot-gap-${i}`}>
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#F59E0B]/15 text-sm font-extrabold text-[#F59E0B]">{i + 1}</span>
                <div>
                  <p className="font-bold text-white">{g.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/70">{g.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </Section>

        <Section n={4} Icon={ArrowRight} title="What you need to do next" testId="welcome-snapshot-next">
          <ol className="space-y-3">
            {snap.next_steps.map((s, i) => (
              <li key={i} className="flex gap-4 rounded-xl border border-emerald-400/20 bg-emerald-400/[0.06] p-4" data-testid={`welcome-snapshot-step-${i}`}>
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-400/15 text-sm font-extrabold text-emerald-300">{i + 1}</span>
                <div>
                  <p className="font-bold text-white">{s.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/70">{s.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </Section>

        <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
          <p className="text-sm text-white/70">The full report verifies these figures against your documents, scores all six pillars and shortlists the funders who fit.</p>
          <button onClick={goReport} data-testid="welcome-snapshot-cta"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#F59E0B] px-7 py-3.5 text-sm font-bold text-[#04302E] shadow-lg shadow-black/30 transition-colors hover:bg-amber-400">
            Get my full Capital Readiness Report <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
